import React, { useState, useRef } from "react";
import { Navigation } from "../components/Navigation";
import { COPY } from "../constants/copy";

interface SurpriseSectionProps {
  onNext: () => void;
  onPrevious: () => void;
}

export const SurpriseSection: React.FC<SurpriseSectionProps> = ({
  onNext,
  onPrevious,
}) => {
  const [revealed, setRevealed] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const cardRef = useRef<HTMLDivElement>(null);
  const [, setGlowPos] = useState({ x: 0, y: 0 });

  // Track mouse movement for interactive glow effect
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current || revealed) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setMousePos({ x, y });
    setGlowPos({ x: x - rect.width / 2, y: y - rect.height / 2 });
  };

  const handleMouseLeave = () => {
    setMousePos({ x: -100, y: -100 });
  };

  const handleReveal = () => {
    setRevealed(true);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-warmWhite dark:bg-darkBg transition-colors duration-400 py-20 relative overflow-hidden">
      {/* Animated background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute w-96 h-96 bg-softRose dark:bg-heartRed opacity-5 rounded-full blur-3xl animate-pulse"
          style={{ top: "-20%", right: "-10%" }}
        ></div>
        <div
          className="absolute w-96 h-96 bg-heartRed dark:bg-softRose opacity-5 rounded-full blur-3xl animate-pulse"
          style={{ bottom: "-20%", left: "-10%" }}
        ></div>
      </div>

      <div className="max-w-2xl w-full space-y-8 relative z-10">
        {/* Animated title */}
        <div
          className={`text-center space-y-3 transition-all duration-500 ${
            revealed ? "scale-95 opacity-70" : "scale-100 opacity-100"
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-light text-deepCharcoal dark:text-darkText">
            One more thing
          </h2>
          <div className="h-px w-16 bg-gradient-to-r from-transparent via-heartRed to-transparent dark:via-softRose mx-auto"></div>
          <p className="text-sm text-mutedGray dark:text-gray-400 font-light tracking-wider">
            {revealed ? "Take this with you" : "Something for you"}
          </p>
        </div>

        {/* Interactive cinematic card */}
        <div
          ref={cardRef}
          onClick={handleReveal}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className={`
            relative h-96 w-full rounded-2xl cursor-pointer flex items-center justify-center
            transition-all duration-700 transform overflow-hidden group
            ${
              revealed
                ? "shadow-2xl scale-100"
                : "shadow-2xl hover:shadow-3xl hover:-translate-y-2"
            }
          `}
        >
          {/* Base gradient background */}
          <div
            className={`absolute inset-0 transition-all duration-700 ${
              revealed
                ? "bg-gradient-to-br from-blushPink via-softRose to-heartRed dark:from-darkCard dark:via-gray-700 dark:to-heartRed"
                : "bg-gradient-to-br from-heartRed via-softRose to-blushPink dark:from-heartRed dark:via-gray-800 dark:to-gray-700"
            }`}
          ></div>

          {/* Interactive glow effect (only when not revealed) */}
          {!revealed && (
            <div
              className="absolute w-32 h-32 bg-white dark:bg-white opacity-20 rounded-full blur-3xl transition-all duration-200 pointer-events-none"
              style={{
                left: `${mousePos.x - 64}px`,
                top: `${mousePos.y - 64}px`,
              }}
            ></div>
          )}

          {/* Shine overlay effect */}
          {!revealed && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transition-opacity duration-700 -skew-x-12"></div>
          )}

          {/* Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center px-8 z-10">
            {revealed ? (
              <div className="space-y-6 text-center animate-fade-in">
                {/* Large quote icon effect */}
                <div className="text-5xl opacity-30 mb-4">✦</div>

                {/* Main text */}
                <p className="text-2xl md:text-3xl text-white dark:text-warmWhite font-light leading-relaxed">
                  {COPY.surprise.text}
                </p>

                {/* Divider */}
                <div className="h-px w-12 bg-white opacity-50 mx-auto mt-6"></div>

                {/* Subtext */}
                <p className="text-sm text-white dark:text-gray-100 font-light tracking-widest uppercase opacity-70">
                  From my heart to yours
                </p>
              </div>
            ) : (
              <div className="text-center space-y-6">
                {/* Animated icons */}
                <div className="relative">
                  <div className="text-7xl animate-pulse">✨</div>
                  <div
                    className="absolute -top-4 -right-4 text-4xl animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  >
                    ✦
                  </div>
                </div>

                {/* Tap text */}
                <div className="space-y-2">
                  <p className="text-white text-lg font-light tracking-wider uppercase">
                    Tap to reveal
                  </p>
                  <p className="text-white text-xs font-light opacity-70 tracking-widest">
                    Something special
                  </p>
                </div>

                {/* Animated pulsing border indicator */}
                <div className="flex justify-center gap-1 pt-2">
                  <div className="w-2 h-2 rounded-full bg-white animate-pulse opacity-70"></div>
                  <div
                    className="w-2 h-2 rounded-full bg-white animate-pulse opacity-70"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                  <div
                    className="w-2 h-2 rounded-full bg-white animate-pulse opacity-70"
                    style={{ animationDelay: "0.4s" }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Status text */}
        <div
          className={`text-center transition-all duration-500 ${
            revealed ? "opacity-100 scale-100" : "opacity-60 scale-95"
          }`}
        >
          {revealed && (
            <p className="text-sm text-mutedGray dark:text-gray-400 font-light italic animate-fade-in">
              This is what matters most.
            </p>
          )}
        </div>

        {/* Button */}
        <div className="flex justify-center pt-2">
          <Navigation
            onNext={revealed ? onNext : handleReveal}
            onPrevious={onPrevious}
            nextLabel={revealed ? "Continue" : "Reveal"}
            showPrevious={true}
          />
        </div>

        {/* Subtle instruction when unrevealed */}
        {!revealed && (
          <p className="text-center text-xs text-mutedGray dark:text-gray-500 font-light animate-pulse uppercase tracking-widest">
            Hover or tap the card above
          </p>
        )}
      </div>
    </div>
  );
};
